import { useState, useCallback } from 'react';
import axiosInstance from '../axiosInstance';
import { endpoints } from '../endPoints';
import useToggle from 'shared/hooks/use-toggle';
import { toast } from 'react-hot-toast';

const unwrapRecords = (axiosResponse) => {
  const records = axiosResponse?.data?.data;
  if (!records || typeof records !== 'object') {
    return { rows: [], total: 0, page: 0, size: 0 };
  }
  return {
    rows: Array.isArray(records.data) ? records.data : [],
    total: Number(records.records) || 0,
    page: records.page ?? 0,
    size: records.size ?? 0,
  };
};

const usePermissionService = () => {
  const [permissions, setPermissions] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, toggleLoading] = useToggle();

  const fetchPermissions = useCallback(
    async (page, size, status = null) => {
      toggleLoading();
      try {
        const base = endpoints.permissions.list;
        const url =
          status != null && String(status).trim() !== ''
            ? `${base}?page=${page}&size=${size}&status=${encodeURIComponent(status)}`
            : `${base}?page=${page}&size=${size}`;
        const response = await axiosInstance.get(url);
        const { rows, total } = unwrapRecords(response);
        setPermissions(rows);
        setTotalRecords(total);
      } catch (error) {
        const msg =
          error.response?.data?.message ||
          error.message ||
          'Failed to load permissions.';
        toast.error(typeof msg === 'string' ? msg : 'Failed to load permissions.');
        console.error('Fetch permissions error:', error);
        setPermissions([]);
        setTotalRecords(0);
      } finally {
        toggleLoading();
      }
    },
    [toggleLoading]
  );

  const createPermission = useCallback(async (payload) => {
    const response = await axiosInstance.post(endpoints.permissions.list, payload);
    return response.data?.data ?? response.data;
  }, []);

  const updatePermission = useCallback(async (payload) => {
    const response = await axiosInstance.put(endpoints.permissions.list, payload);
    return response.data?.data ?? response.data;
  }, []);

  const changePermissionStatus = useCallback(async (id, status) => {
    const response = await axiosInstance.post(
      `${endpoints.permissions.status}?id=${encodeURIComponent(id)}&status=${encodeURIComponent(status)}`
    );
    return response.data?.data ?? response.data;
  }, []);

  const deletePermission = useCallback(async (id) => {
    const response = await axiosInstance.delete(
      `${endpoints.permissions.list}/${encodeURIComponent(id)}`
    );
    return response.data?.data ?? response.data;
  }, []);

  return {
    permissions,
    totalRecords,
    isLoading,
    fetchPermissions,
    createPermission,
    updatePermission,
    changePermissionStatus,
    deletePermission,
  };
};

export default usePermissionService;
